import { ChangeDetectorRef, Component, DestroyRef, Input, Output, EventEmitter, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, debounceTime, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import { ReactiveFormsModule, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SearchableSelectComponent } from '../../ui-primitives/searchable-select/searchable-select.component';
import { FieldValue, FieldOption, RecordField, RecordSection, UserRole } from '../cfi-record.model';
import { countSsnDigits, formatSsnForDisplay, ssnCaretPosition } from '../cfi-record-ssn.util';
import { matchesCondition } from '../cfi-record-condition.util';
import { isLabelValued } from '../cfi-record-select.util';

/**
 * Error keys emitted by @k12/validators (and Angular's `pattern`), mapped to the sentence fragment
 * appended after the field label. Without this, an unrecognised key would surface to the user as the
 * raw key, e.g. "invalidZipCode".
 */
const FORMAT_ERROR_MESSAGES: Record<string, string> = {
  invalidSSN: 'must be a valid Social Security Number (XXX-XX-XXXX)',
  invalidZipCode: 'must be a valid ZIP code',
  invalidPhone: 'must be a valid US phone number',
  invalidEmail: 'must be a valid email address',
  invalidUrl: 'must be a valid URL',
  invalidFEIN: 'must be a valid FEIN',
  invalidHHSize: 'must be a valid household size',
  invalidNumberOrDecimal: 'must be a number',
  invalidNumber: 'must be a valid PowerSchool UID',
  invalidAlphanumeric: 'must contain only letters and numbers',
  duplicateValue: 'must be unique',
  pattern: 'format is invalid'
};

@Component({
  selector: 'cfi-record-edit',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    SearchableSelectComponent
  ],
  templateUrl: './cfi-record-edit.html',
  styleUrls: ['./cfi-record-edit.scss']
})
export class CfiRecordEditComponent implements OnInit {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) sections!: RecordSection[];
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) options!: Record<string, FieldOption[]>;
  @Input() userRole: UserRole | null = null;
  @Input() pageTitle = '';
  @Input() prefix = 'field';
  @Input() isSaving = false;

  @Input() searchHandlers: Record<string, (query: string) => Observable<FieldOption[]>> = {};

  /**
   * Known option for a `search-select` field's current value, keyed by optionsKey, so the dropdown
   * reads correctly before its options load — or when the value is missing from them entirely, as an
   * active-only lookup does for an inactive record.
   */
  @Input() selectedOptions: Record<string, FieldOption | null> = {};

  @Output() save = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  searching: Record<string, boolean> = {};

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.sections
      .flatMap((section) => section.fields)
      .filter((field) => field.type === 'autocomplete' && field.asyncSearch && !!field.optionsKey)
      .forEach((field) => this.wireAsyncSearch(field));
  }

  private wireAsyncSearch(field: RecordField): void {
    const optionsKey = field.optionsKey!;
    const handler = this.searchHandlers[optionsKey];
    const control = this.form.get(field.key);
    if (!handler || !control) return;

    const search = (query: string) => handler(query).pipe(catchError(() => of<FieldOption[]>([])));

    // mat-autocomplete doesn't refresh its open panel when options change on a normal CD tick,
    // so force a synchronous check when the search state changes.
    const setSearching = (isSearching: boolean) => {
      this.searching[optionsKey] = isSearching;
      this.cdr.detectChanges();
    };
    const applyResults = (options: FieldOption[]) => {
      this.options[optionsKey] = options;
      this.searching[optionsKey] = false;
      this.cdr.detectChanges();
    };

    const initial = control.value;
    if (typeof initial === 'string' && initial.length > 0) {
      setSearching(true);
      search(initial).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(applyResults);
    }

    control.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap((value: FieldValue | FieldOption) => setSearching(typeof value === 'string' && value.length >= 2)),
        switchMap((value: FieldValue | FieldOption) =>
          typeof value === 'string' && value.length >= 2 ? search(value) : of<FieldOption[]>([])
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(applyResults);
  }

  getValue(field: RecordField): FieldValue {
    return (this.form.get(field.key)?.value as FieldValue) ?? null;
  }
  
  isVisible(field: RecordField): boolean {
    if (this.userRole !== 'admin' && (!this.userRole || !field.editableBy?.includes(this.userRole))) return false;
    if (!field.showWhen) return true;
    const { field: watchField, is } = field.showWhen;
    return matchesCondition(this.form.get(watchField)?.value as FieldValue, is);
  }

  /**
   * A field is readonly if it's explicitly marked as readonly, or if its readonlyWhen condition evaluates to true.
   * When a field is readonly, it should render as non-editable and display an optional message if provided.
   */
  isReadonly(field: RecordField): boolean | undefined {
    if (!field.readonlyWhen) return field.readonly;
    const { field: watchField, is } = field.readonlyWhen;
    return matchesCondition(this.form.get(watchField)?.value as FieldValue, is);
  }

  /**
   * Rewrites a formatted field's value on every keystroke so the user sees the grouped form
   * (111-23-3232) while typing. The caret is re-anchored to the character it was behind, otherwise
   * inserting a separator would push it to the end of the input.
   */
  onFormattedInput(field: RecordField, event: Event): void {
    if (!field.format) return;

    const input = event.target as HTMLInputElement;
    const control = this.form.get(field.key);
    if (!control) return;

    const digitsBeforeCaret = countSsnDigits(input.value.slice(0, input.selectionStart ?? input.value.length));
    const formatted = formatSsnForDisplay(input.value);

    control.setValue(formatted);
    input.value = formatted;

    const caret = ssnCaretPosition(formatted, digitsBeforeCaret);
    input.setSelectionRange(caret, caret);
  }

  getOptions(field: RecordField): FieldOption[] {
    if (field.type === 'boolean') return this.options['yesNo'] ?? [];
    if (!field.optionsKey) return [];
    const options = this.options[field.optionsKey] ?? [];
    if (!isLabelValued(options)) return options;

    const value = this.getValue(field);
    if (value == null || value === '') return options;
    if (options.some((o) => o.value === value)) return options;
    return [...options, { label: String(value), value }];
  }

  selectedOptionFor(field: RecordField): FieldOption | null {
    return (field.optionsKey && this.selectedOptions[field.optionsKey]) || null;
  }

  hasSearchQuery(field: RecordField): boolean {
    const value = this.form.get(field.key)?.value;
    return typeof value === 'string' && value.length >= 2;
  }

  errorFor(field: RecordField): string | null {
    const ctrl = this.form.get(field.key);
    if (!ctrl?.errors || !ctrl.touched) return null;

    const errors = ctrl.errors;
    if (errors['required']) return `${field.label} is required`;
    if (errors['min']) return `${field.label} must be at least ${errors['min'].min}`;
    if (errors['max']) return `${field.label} must be at most ${errors['max'].max}`;
    if (errors['maxlength']) {
      return `${field.label} must be ${errors['maxlength'].requiredLength} characters or fewer`;
    }
    if (errors['name']) {
      return (errors['name'] as { message?: string }).message
        ?? `${field.label} format is invalid`;
    }
    if (errors['message']) return errors['message'] as string;

    const formatKey = Object.keys(errors).find((key) => key in FORMAT_ERROR_MESSAGES);
    if (formatKey) return `${field.label} ${FORMAT_ERROR_MESSAGES[formatKey]}`;

    return `${field.label} is invalid`;
  }

  /**
   * Id of this field's error element, or null when no error is showing, so that
   * aria-describedby only points at text the user can actually see.
   */
  errorId(field: RecordField): string | null {
    return this.errorFor(field) ? `${this.prefix}_${field.key}_error` : null;
  }

  /** Returns null rather than false so the attribute is omitted entirely when the field is valid. */
  isInvalid(field: RecordField): true | null {
    return this.errorFor(field) ? true : null;
  }

  isRequired(field: RecordField): true | null {
    return this.form.get(field.key)?.hasValidator(Validators.required) ? true : null;
  }

  trackBySection(_: number, section: RecordSection): string {
    return section.title;
  }

  trackByField(_: number, field: RecordField): string {
    return field.key;
  }

  displayWith = (value: FieldOption | string | null): string => {
    if (value == null) return '';
    if (typeof value === 'object') return value.label;
    return String(value);
  };

  displayValue(field: RecordField): string {
    const value = this.getValue(field);

    if (
      field.type !== 'select' &&
      field.type !== 'search-select' &&
      field.type !== 'autocomplete' &&
      (value == null || value === '')
    ) {
      return '—';
    }

    switch (field.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'currency':
        return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'date':
        return new Date(String(value)).toLocaleDateString('en-US');
      case 'select':
      case 'search-select': {
        if (value == null) return '—';
        const known = this.selectedOptionFor(field);
        if (known?.value === value && known.label) return known.label;
        const options = this.getOptions(field);
        if (!options.length) return String(value);
        const matched = options.find((o) => o.value === value);
        if (matched) return matched.label;
        return isLabelValued(options) ? String(value) : '—';
      }
      case 'autocomplete': {
        if (value == null || value === '') return '—';
        if (typeof value === 'object') return (value as FieldOption).label ?? '—';
        return String(value);
      }
      default:
        return String(value);
    }
  }
}
